import React, { useState } from 'react';

import { useBaseWidget } from 'hooks/useBaseWidget';

import { TooltipPosition } from 'components/common/CustomizedTooltip/customizedTooltip.enums';
import { ExportDropdown } from 'components/common/ExportDropdown/exportDropdown';
import NoData from 'components/common/NoData/noData';
import { ShareInsightButton } from 'components/common/ShareInsightButton/ShareInsightButton';
import SwitchComponent from 'components/common/Switch/Switch';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';
import { MuiIcon } from 'components/common/muiIcon/muiIcon';

import { AddWidget } from 'components/Dashboards/Project/Components/CustomDashboard/AddRemoveWidget/addWidget';
import { PinboardWidgetPills } from 'components/Dashboards/Project/Components/CustomDashboard/PinboardWidgetPills/PinboardWidgetPills';
import { IComponentProps } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { CustomizedRoundIcon, CustomizedTooltip, MyCustomSelect } from 'components/common';
import { IClusterItem } from 'components/widgets/Gantt/components/ganttChart.types';
import { GanttSettingsModal } from 'components/widgets/Gantt/components/ganttSettingsModal';
import { useSharedResourcesAvailabilityLogic } from 'components/widgets/SharedResourcesAvailability/SharedResourcesAvailability.logic';
import classes from 'components/widgets/SharedResourcesAvailability/SharedResourcesAvailability.module.scss';
import { useSharedResourcesAvailabilityProvider } from 'components/widgets/SharedResourcesAvailability/SharedResourcesAvailability.provider';
import GanttChartDisplay from 'components/widgets/SharedResourcesAvailability/ganttChartDisplay/ganttChartDisplay';

const componentKey = 'sharedResourcesAvailability';
const title = 'Shared Resources Availability For Overlapping Activities';
const id = 'shared-resources-availability';

export const SharedResourcesAvailability = ({
    latestVersionId,
    widgetId,
    externalComponents,
    projectId,
    contractId,
    localFilters,
    editNarrative,
    setSubTitleForExport,
    globalFilterData,
}: IComponentProps) => {
    const [showSettings, setShowSettings] = useState(false);

    const { subTitle, route } = useBaseWidget({
        id,
        widgetId,
        projectId,
        contractId,
        latestVersionId,
        title,
        setSubTitleForExport,
    });

    const { ui, api } = useSharedResourcesAvailabilityProvider({
        widgetId,
        latestVersionId,
        localFilters,
    });
    const { logic } = useSharedResourcesAvailabilityLogic({ ui, api, globalFilterData });

    return (
        <>
            <WidgetWithTitle
                id={id}
                title={title}
                tooltip={api.getSharedResourcesAvailability.data?.info}
                titleComponents={[
                    externalComponents && <div key={'externalComponents'}>{externalComponents}</div>,
                    <CustomizedTooltip
                        key={'zoomToFit'}
                        tooltipPosition={TooltipPosition.Top}
                        tooltipContent={`Zoom To Fit`}
                        triggerElement={
                            <CustomizedRoundIcon onClick={logic.zoomToFit}>
                                <MuiIcon icon={'zoom_in_map'} fontSize={'2.4rem'} />
                            </CustomizedRoundIcon>
                        }
                    />,
                    <CustomizedTooltip
                        key={'fullScreen'}
                        tooltipPosition={TooltipPosition.Top}
                        tooltipContent={`Full Screen`}
                        triggerElement={
                            <CustomizedRoundIcon onClick={logic.handleFullscreen}>
                                <MuiIcon icon={'fullscreen'} fontSize={'2.8rem'} />
                            </CustomizedRoundIcon>
                        }
                    />,
                    !widgetId && (
                        <CustomizedTooltip
                            tooltipPosition={TooltipPosition.Top}
                            tooltipContent={`Gantt Settings`}
                            triggerElement={
                                <CustomizedRoundIcon onClick={() => setShowSettings(true)}>
                                    <MuiIcon icon={'tune'} fontSize={'2.4rem'} />
                                </CustomizedRoundIcon>
                            }
                        />
                    ),
                    <AddWidget
                        key={'addWidget'}
                        title={title}
                        componentKey={componentKey}
                        projectId={projectId}
                        contractId={contractId}
                        widgetId={widgetId}
                        route={route}
                        localFilters={ui.state}
                    />,
                    <ShareInsightButton key={'shareInsight'} title={title} link={route} />,
                    <ExportDropdown title={title} key={'export'} subTitle={subTitle} />,
                ]}
                titleFilters={[
                    editNarrative && <div key={'editNarrative'}>{editNarrative}</div>,
                    widgetId && ui.pills.length > 0 ? (
                        <PinboardWidgetPills key={'widgetFilters'} widgetId={widgetId} filters={ui.pills} />
                    ) : (
                        <div className={classes.localFilters} key={'localFilters'}>
                            <div className={classes.clusterSelectFilterWrapper}>
                                <label>
                                    Select Cluster
                                    <span className={classes.countOfTotal}>
                                        {logic.selectedClusterCountOfTotalTitle}
                                    </span>
                                </label>
                                <MyCustomSelect<IClusterItem>
                                    value={ui.state.cluster}
                                    options={logic.clustersList}
                                    onChange={ui.setters.setCluster}
                                    className={classes.clusterSelect}
                                />
                            </div>

                            <div className={classes.criticalPathFilterWrapper}>
                                Critical Path Only
                                <SwitchComponent
                                    onChange={(e) => ui.setters.setIsCriticalPath(e.target.checked)}
                                    checked={ui.state.isCriticalPath}
                                />
                            </div>
                        </div>
                    ),
                ]}
            >
                <div ref={logic.ref}>
                    {logic.isAnyGlobalFilterApplied ? (
                        <NoData>Not applicable with the applied filter</NoData>
                    ) : (
                        <GanttChartDisplay
                            isFullScreen={logic.isFullScreen}
                            ganttData={api.getSharedResourcesAvailability.data}
                            isLoading={api.getSharedResourcesAvailability.isFetching}
                            getInstanceAgain={logic.getInstance}
                            isCriticalPath={ui.state.isCriticalPath}
                            selectedCluster={logic.selectedCluster}
                        />
                    )}
                </div>
            </WidgetWithTitle>
            <GanttSettingsModal showModal={showSettings} onHideModal={() => setShowSettings(false)} />
        </>
    );
};
